import { useState } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Text, VStack } from '@enechain/ecloud-designsystem'

import { BidCsvDateCounts } from '~/components/bidding/common/BidCsvDateCounts'
import { BidCsvImportExcludedBanner } from '~/components/bidding/common/BidCsvImportExcludedBanner'
import { CompactAlert } from '~/components/common/CompactAlert'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { UploadWizard } from '~/components/upload/UploadWizard'
import { MAX_CSV_FILE_SIZE_BYTES } from '~/constants/csvUpload'
import { useBidCsvImportReturn } from '~/hooks/useBidCsvImportReturn'
import { downloadCsv } from '~/libs/downloadCsv'
import {
  type BidCsvDateCount,
  toBidCsvDateCounts,
} from '~/models/bidCsvDateCount'
import {
  INTRADAY_ORDER_CSV_HEADER,
  INTRADAY_ORDER_CSV_HEADER_BY_LANG,
  INTRADAY_ORDER_CSV_HEADER_EN,
} from '~/models/bidCsvHeader'
import {
  type BidCsvImportNotice,
  buildIntradayNotice,
} from '~/models/bidCsvImportReturn'
import { translateIntradayServerError } from '~/models/intradayServerError'
import { CsvConfirmRejection } from '~/pages/IntradayCsvImport/components/CsvConfirmRejection'
import { CsvInversionAlert } from '~/pages/IntradayCsvImport/components/CsvInversionAlert'
import { CsvUploadErrorBanner } from '~/pages/IntradayCsvImport/components/CsvUploadErrorBanner'
import { PreviewTable } from '~/pages/IntradayCsvImport/components/PreviewTable'
import {
  useApplyIntradayCsv,
  useCheckIntradayCsv,
} from '~/pages/IntradayCsvImport/hooks/useIntradayCsvMutations'
import { readIntradayConfirmRejection } from '~/pages/IntradayCsvImport/models/confirmRejection'
import {
  type IntradayCsvInversionCandidate,
  toIntradayCsvInversionCandidates,
} from '~/pages/IntradayCsvImport/models/csvInversion'
import { intradayCsvColumnGuide } from '~/pages/IntradayCsvImport/models/intradayCsvColumnGuide'
import {
  INTRADAY_CSV_TEMPLATE_SAMPLE_DELIVERY_DATE,
  buildIntradayCsvTemplate,
  formatIntradayCsvTargetDate,
  intradayCsvTemplateFileName,
  toIntradayPreviewRows,
} from '~/pages/IntradayCsvImport/models/intradayCsvImport'
import { routePaths } from '~/routes/routePaths'

const INTRADAY_ORDER_CSV_ACCEPTED_HEADERS = {
  ja: [INTRADAY_ORDER_CSV_HEADER_BY_LANG.ja],
  en: [INTRADAY_ORDER_CSV_HEADER_BY_LANG.en],
}

type CheckResult = {
  checkToken: string
  rows: ReturnType<typeof toIntradayPreviewRows>
  /** 取込対象の札数。 */
  importableCount: number
  /** 対象外行数 (GC済み / 発注主体区分未登録のいずれか。 AC-BCI-004b/010)。 */
  excludedCount: number
  errorCount: number
  /** 受渡日ごとの取込対象件数 (AC-BCI-002)。 新規の行が無ければ空。 */
  dateCounts: BidCsvDateCount[]
  /** 売買逆転の判定に渡す、 取り込む行 (AC-BCI-035 ※)。 */
  inversionCandidates: IntradayCsvInversionCandidate[]
  /**
   * 手数料自動考慮（時間前）が ON で手数料単価を取得できていない
   * (intraday-bidding.md AC-BIN-004b)。 true の間は確定操作を無効化する
   * (行のエラーとは別に、 取込全体の可否を示すフラグ)。
   */
  feeAutoConsiderUnavailable: boolean
}

type ApplyResult = {
  /** 入札画面へ移った先 (0 件なら取込画面) に出す取込結果 (AC-BCI-038 / AC-BCI-039) */
  notice: BidCsvImportNotice
}

/**
 * 時間前入札 CSV インポート画面
 * (`/bidding/intraday-csv-import`、 design `bidding/intraday-csv-import.html`)。
 *
 * 配置はデータ取込ハブ (`admin/data/uploads/index.md`) ではなく **時間前入札画面の
 * 子ページ** (spec intraday-csv-import.md §6。 スポット CSV アップロードと同じ
 * 配置方針)。 遷移元は入札作成タブの「札作成」ドロップダウンの「CSVインポート」。
 *
 * スポット CSV 画面 (`SpotCsvImport`) との違い:
 * - 列マッピング UI も種別 (`?kind=`) も持たない。 時間前には対応する eSLJ 入稿
 *   仕様の運用実態が無いため、 固定ヘッダー 15 列の 1 種類だけ (spec §2)
 * - **発注主体セレクタを持たない**。 発注主体は行ごとの `発注主体区分` 列で決まる
 *   ため画面上部での事前選択を行わない (spec §6 / AC-BCI-004a、 2026-08-14 PdM 判断)
 * - 確定は下書きに留めず、 行ごとのエリアの承認モードに従って提出まで進む
 *   (要承認は承認待ち、 承認なし・自動は入札中〈開場前の受渡日は予約中〉。
 *   spec §5 / AC-BCI-011、 2026-09-27 PdM 判断)
 *
 * 受渡日はスポットと同じく行ごとの `受渡日` 列で決まり、 入札追加タブで選択中の
 * 受渡日は引き継がない (query を読まず、 Check にも渡さない。 spec §6 / AC-BCI-002、
 * intraday-bidding.md AC-BIN-102)。 Step2 に受渡日ごとの件数を出す。
 *
 * Step 構成・エラーハンドリング・check_token の扱いは共通の `UploadWizard`
 * (= `admin/data/uploads/common.md` の実装) に委譲し、 本画面は Check/Apply RPC と
 * プレビュー整形だけを持つ。
 */
export const IntradayCsvImportPage = (): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language.startsWith('en')
  const returnToBidPage = useBidCsvImportReturn(routePaths.biddingIntraday.path)

  // 確定の結果、 取り込んだ札が 0 件だった時 (AC-BCI-039) のエラーバナー。 入札画面へは移らず、
  // Step1 に戻った取込画面の本文の最上部に出す。 次にファイルを選んだら消す。
  const [zeroImportNotice, setZeroImportNotice] = useState<BidCsvImportNotice>()

  const checkCsv = useCheckIntradayCsv()
  const applyCsv = useApplyIntradayCsv()

  // 売買逆転の判定に要る既存の札を読み終えたチェック結果の、 取り込む行
  // (`CsvInversionAlert` が読み終えた時に渡す)。 どのチェック結果の分かを取り違えない
  // よう、 件数や真偽値ではなく行の配列そのもので持ち、 canProceed で同一性を見る。
  const [inversionCheckedCandidates, setInversionCheckedCandidates] =
    useState<IntradayCsvInversionCandidate[]>()

  // 受付開始前の受渡日 (AC-BCI-011a) を含め、 行ごとの検証結果は backend が行エラーの
  // 文言で返し、 プレビューにそのまま出す。 取込全体の拒否 (ヘッダー不一致・JEPX会員
  // 情報の未登録 等) も backend が画面向けの文言を返すので訳さない。
  const handleCheck = async (file: File): Promise<CheckResult> => {
    setZeroImportNotice(undefined)
    const res = await checkCsv.mutateAsync({
      csvContent: new Uint8Array(await file.arrayBuffer()),
      csvFileName: file.name,
    })
    return {
      checkToken: res.checkToken,
      rows: toIntradayPreviewRows(res.rows),
      importableCount: res.createdCount,
      excludedCount: res.excludedCount,
      errorCount: res.errorCount,
      dateCounts: toBidCsvDateCounts(
        res.dateCounts,
        formatIntradayCsvTargetDate,
      ),
      inversionCandidates: toIntradayCsvInversionCandidates(res.rows),
      feeAutoConsiderUnavailable: res.feeAutoConsiderUnavailable,
    }
  }

  /**
   * Step1: ヘッダー行 + spec §2 の記入例 4 行の雛形を DL する。 表示言語が英語の
   * ときはヘッダー行・区分値・ファイル名も英語にする (spec §2「英語表示対応」、 common.md §6)。
   */
  const handleTemplateDownload = (): void => {
    downloadCsv(
      intradayCsvTemplateFileName(isEnglish),
      buildIntradayCsvTemplate(isEnglish),
    )
  }

  const handleApply = async (checkToken: string): Promise<ApplyResult> => {
    try {
      const res = await applyCsv.mutateAsync({ checkToken })
      return { notice: buildIntradayNotice(res.rows, res.createdCount) }
    } catch (err) {
      // `UploadWizard` は NotFound (= check_token 期限切れ) / AlreadyExists
      // (= 重複登録) を code で判定して専用 UI に振り分けるので、 その 2 code は
      // そのまま投げ直して素通しする。 それ以外は個別作成画面と同じ
      // registry (`translateIntradayServerError`) で日本語文言へ訳してから
      // 投げ直す — `UploadWizard` は `error.rawMessage` をそのまま画面に出すため、
      // 素通しすると backend の内部エラー文言 (英語、 例: "intraday_order_usecase:
      // create after gate closure (GC) is not allowed") がそのままユーザーに見えて
      // しまう (Apply は確定時点で GC / 受付開始ガードを再評価する `u.Create` を
      // 経由するため、 CSV 経由でもこの内部メッセージが起こりうる)。
      //
      // 投げ直す時も code は変えない: `UploadWizard` は、 結果が返らなかった失敗
      // (通信エラー・システムエラー) かどうかを code で見分ける (`awaitsApplyResult`)。
      if (
        err instanceof ConnectError &&
        (err.code === Code.NotFound || err.code === Code.AlreadyExists)
      ) {
        throw err
      }
      // 確定時の再検証で取込全体を拒否された時 (行エラーとファイル全体の理由が
      // error detail で届く)。 汎用文言に潰さず、 確定モーダルが全件出せるよう
      // error ごと素通しする (issue #8786 Des 判断 1)。
      if (readIntradayConfirmRejection(err) !== undefined) {
        throw err
      }
      const message = translateIntradayServerError(err, t)
      throw new ConnectError(
        message,
        err instanceof ConnectError ? err.code : Code.Unknown,
      )
    }
  }

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:intradayCsvImport.breadcrumb.bidding') },
        {
          label: t('page:intradayCsvImport.breadcrumb.intraday'),
          to: routePaths.biddingIntraday.path,
        },
        { label: t('page:intradayCsvImport.breadcrumb.title'), bold: true },
      ]}
    >
      <PageBody>
        {zeroImportNotice !== undefined && (
          <BidCsvImportExcludedBanner
            marginBottom={4}
            notice={zeroImportNotice}
          />
        )}
        {/* 確定後の状態 (行ごとのエリアの承認モードに従って承認待ち・入札中・
            予約中のいずれかになること) は確定モーダルにも書かれている業務上の
            要点なので、 常時見える位置に置く (spec §5、 2026-09-27 PdM 判断。
            design/bidding/intraday-csv-import.html は旧仕様〈確定後は未提出〉の
            ままで未更新 — spec Discussion の新しい決定を優先する)。 */}
        <Text marginBottom={4} color="Text.midEmphasis" fontSize="md">
          {t('page:intradayCsvImport.submitNotice')}
        </Text>
        <UploadWizard<CheckResult, ApplyResult>
          // アップロードは表示言語に関わらず日本語・英語のどちらのヘッダーも受理する
          // (AC-BCI-001)。 不一致の文言に出す期待ヘッダーは表示言語の列名にする。
          acceptedHeaders={INTRADAY_ORDER_CSV_ACCEPTED_HEADERS}
          // 入札 CSV の確定は結果を受け取るまで待つ (common.md AC-DUC-039〜041。
          // 共通仕様の非同期の確定の例外)。 結果を待つ間は画面を閉じないよう案内し、 確定が
          // 失敗した時は、 どの失敗でも同じ引換券で確定し直させず、 ファイルの選び直しにだけ
          // 進める (取込全体の拒否で backend は引換券を消費する。 結果が返らなかった時は、
          // 取り込まれたかどうかをアップロード履歴で確かめてもらう)。
          awaitsApplyResult
          // common.md §4: 業務バリデーションエラーがある間は確定へ進ませない。
          // Check 時点で取込対象 0 件 (= ヘッダーのみの CSV・全行が対象外) も
          // 同様に進ませない。
          //
          // Check 後に全行が対象外になった時は、 確定側が 0 件の成功と実結果を返し、
          // AC-BCI-039 に従って Step1 に戻して対象外理由を表示する。
          //
          // 売買逆転の判定に要る既存の札を読み終えるまでも進ませない (読み終える前は、
          // 既存の札との売買逆転を警告できないまま確定できてしまう)。 判定に渡す行が
          // 無い時 (新規の行が無い・基準価格の行だけ) は待たない。
          //
          // AC-BIN-004b: 手数料自動考慮 ON で手数料単価を取得できていない間も進ませない
          // (確定操作の無効化)。 確定側も同じ時に取込全体を拒否し、 引換券も消費する。
          canProceed={(result) =>
            result.errorCount === 0 &&
            result.importableCount > 0 &&
            !result.feeAutoConsiderUnavailable &&
            (result.inversionCandidates.length === 0 ||
              result.inversionCandidates === inversionCheckedCandidates)
          }
          confirmDialog={{
            openLabel: t('page:intradayCsvImport.confirmModal.open'),
            title: t('page:intradayCsvImport.confirmModal.title'),
            confirmLabel: t('page:intradayCsvImport.confirmModal.commit'),
            renderBody: (result) => (
              <VStack align="stretch" gap={2}>
                <Text>{t('page:intradayCsvImport.confirmModal.body')}</Text>
                <Text textStyle="label">
                  {t('page:intradayCsvImport.confirmModal.count', {
                    count: result.importableCount,
                  })}
                </Text>
              </VStack>
            ),
          }}
          displayHeader={isEnglish ? INTRADAY_ORDER_CSV_HEADER_EN : undefined}
          downloads={[{ onClick: handleTemplateDownload }]}
          // spec §1: 1 行ヘッダー構造 (スポットブロックの eSLJ 互換 2 行ヘッダーと違う)。
          expectedHeader={INTRADAY_ORDER_CSV_HEADER}
          // common.md AC-DUC-037: Step1 に記入ガイド表 (spec §3) を出す。
          // 英語表示では英語の表にする (#9332)。
          guideSections={[{ rows: intradayCsvColumnGuide(isEnglish) }]}
          headerRowIndex={0}
          maxFileSizeBytes={MAX_CSV_FILE_SIZE_BYTES}
          onApply={handleApply}
          onCheck={handleCheck}
          // 確定が成功したら、 すぐ時間前入札画面の入札追加タブへ移る。 移った先の成功の
          // トースト (除いた行があればエラーバナー) が確認になる (AC-BCI-038)。 取り込んだ札が
          // 0 件の時は確かめる札が無いので移らず、 Step1 に戻った取込画面にエラーバナーを出す
          // (AC-BCI-039)。
          onComplete={(result) => {
            if (result.notice.importedCount === 0) {
              setZeroImportNotice(result.notice)
              return
            }
            returnToBidPage(result.notice)
          }}
          // 確定時に取込全体を拒否された時は、 確定モーダルを行番号付きエラーの表示に
          // 切り替える (issue #8786 Des 判断)。 拒否以外の失敗は従来の Alert。
          renderApplyRejection={(_message, error) => {
            const rejection = readIntradayConfirmRejection(error)
            return rejection === undefined ? undefined : (
              <CsvConfirmRejection {...rejection} />
            )
          }}
          renderSummary={(result) => (
            <VStack align="stretch" gap={3}>
              {/* common.md AC-DUC-014: エラー行がある間は確定へ進めない
                  (canProceed)。 その理由と直し方を件数の表示の上に出す
                  (design `#error-banner` の位置)。 */}
              <CsvUploadErrorBanner count={result.errorCount} />
              {/* AC-BIN-004b: 手数料自動考慮 ON で手数料単価を取得できていない間、
                  確定ボタンが無効化される理由を明示する。 */}
              {result.feeAutoConsiderUnavailable && (
                <CompactAlert
                  status="warning"
                  title={t('page:intradayCsvImport.feeAutoConsiderUnavailable')}
                />
              )}
              {/* AC-BCI-035 ※: 売買逆転は警告だけで取込を止めない。 design に
                  置き場が無いので、 件数の表示の上に入札追加タブと同じバナーを出す。 */}
              <CsvInversionAlert
                candidates={result.inversionCandidates}
                onExistingOrdersLoaded={setInversionCheckedCandidates}
              />
              <Text textStyle="label">
                {t('page:intradayCsvImport.summary', {
                  importableCount: result.importableCount,
                  excludedCount: result.excludedCount,
                  errorCount: result.errorCount,
                })}
              </Text>
              {/* AC-BCI-002: 当日分と翌日分を 1 ファイルに入れられるので、 どの受渡日に
                  何件入るかを確定前に見せる (日付の取り違えにここで気づけるように
                  する)。 件数は backend が数えた新規の行だけ。 */}
              <BidCsvDateCounts dateCounts={result.dateCounts} />
              {/* 取込対象 0 件 = 確定へ進めない理由を明示する (件数表示だけだと
                  「取込を確定」が押せない理由が読み取れない)。 全行が対象外
                  (GC済み / 発注主体区分未登録) のケースは、
                  「ヘッダー行だけの CSV」 の案内文言だと実態と食い違うので
                  区別する (AC-BCI-004b/010)。 */}
              {result.importableCount === 0 && result.errorCount === 0 && (
                <CompactAlert
                  status="warning"
                  title={t(
                    result.excludedCount > 0
                      ? 'page:intradayCsvImport.noImportableRowsExcluded'
                      : 'page:intradayCsvImport.noImportableRows',
                  )}
                />
              )}
              <PreviewTable rows={result.rows} />
            </VStack>
          )}
          // common.md AC-DUC-035: 雛形に記入例行が入っていることを案内する。
          sampleRow={{
            column: t('page:intradayCsvImport.columns.targetDate'),
            example: INTRADAY_CSV_TEMPLATE_SAMPLE_DELIVERY_DATE,
          }}
        />
      </PageBody>
    </PageLayout>
  )
}
